import { useState, useContext } from "react"
import { useNavigate } from "react-router-dom"
import { Box, Paper, TextField, Tooltip, IconButton, Grid, MenuItem, Typography } from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, api, showWarning } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"
import Save from "@mui/icons-material/Save"

// Tema estándar de SIACDEV1.0
const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const CrearCentrosPPE = () => {
  const navigate = useNavigate()
  const { selectedMenuInfo } = useContext(GlobalContext)

  // Estado inicial del formulario adaptado a afbcentro
  const [formData, setFormData] = useState({
    centcodigo: "",
    centdescri: "",
    centstatus: "A", // Activo por defecto
  })

  // Mutación para guardar los datos
  const {
    mutateAsync: SaveNuevoCentroActivo,
    isPending: isSavingCentroActivo,
    isError,
  } = useMutation({
    queryKey: ["isCreatingCentroActivo"],
    fn: async (data) => {
      const response = await api.post("/CentrosPPE/createCentrosPPE", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: () => navigate(-1),
  })

  // Manejador de cambios con validación de longitud (strip y upper)
  const handleInputChange = (field, value) => {
    let finalValue = value.toUpperCase()

    if (field === "centcodigo") {
      finalValue = finalValue.slice(0, 4) // Máximo 4 caracteres según BD
    } else if (field === "centdescri") {
      finalValue = finalValue.slice(0, 100) // Máximo 100 caracteres
    }

    setFormData((prev) => ({ ...prev, [field]: finalValue }))
  }

  // Manejador del botón Guardar
  const handleSubmit = async (e) => {
    if (e) e.preventDefault()
    try {
      if (!formData.centcodigo.trim()) {
        showWarning("El Código del Centro es obligatorio")
        return
      }
      if (!formData.centdescri.trim()) {
        showWarning("La Descripción del Centro es obligatoria")
        return
      }

      await SaveNuevoCentroActivo({ ...formData })
    } catch (error) {
      console.error("Error al crear Centro de Activo:", error)
    }
  }

  // Buscar acción de guardar en los permisos del usuario
  const guardarAction = selectedMenuInfo?.data?.barraAcciones?.find(
    (action) =>
      action?.acccaption === "GUARDAR" ||
      action?.acccaption === "EJECUTAR" ||
      action?.acccaption === "CREAR" ||
      action?.acccaption === "NUEVO",
  )

  const toolbarActions = []
  if (guardarAction) {
    toolbarActions.push({
      label: guardarAction.acccaption,
      key: guardarAction.acccaption,
      icon: getIconComponent(guardarAction.accnameicono, guardarAction.acctipoico),
    })
  } else {
    // Fallback por si no encuentra la acción en los permisos
    toolbarActions.push({ label: "Guardar Centro", key: "GUARDAR", icon: <Save /> })
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        {/* BARRA DE HERRAMIENTAS SUPERIOR */}
        <Box sx={{ mb: 2 }}>
          {toolbarActions.map((action) => (
            <Tooltip title={action.label} key={action.key}>
              <IconButton
                onClick={handleSubmit}
                disabled={isSavingCentroActivo}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Crear Centro de Activo (PPE)</b>
        </div>

        <CustomBackdrop isLoading={isSavingCentroActivo} />

        <Box sx={StyledRoot}>
          <Paper
            elevation={3}
            sx={{ p: 4, mb: 3, borderRadius: 3, background: "white" }}
            component="form"
            onSubmit={handleSubmit}
          >
            <Typography variant="h6" color="primary" gutterBottom>
              Datos del Centro
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={3}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Código del Centro *"
                  value={formData.centcodigo}
                  onChange={(e) => handleInputChange("centcodigo", e.target.value)}
                  placeholder="Ej: C001"
                  error={isError && !formData.centcodigo}
                  inputProps={{ maxLength: 4 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Descripción *"
                  value={formData.centdescri}
                  onChange={(e) => handleInputChange("centdescri", e.target.value)}
                  error={isError && !formData.centdescri}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              {/* El campo estado no suele mostrarse en la creación, pero lo añado como campo oculto o select fijo por si decides activarlo */}
              {/* <Grid item xs={12} sm={3}>
                <TextField select InputLabelProps={{ shrink: true }} fullWidth label="Estado" value={formData.centstatus} onChange={(e) => handleInputChange("centstatus", e.target.value)}>
                  <MenuItem value="A">ACTIVO</MenuItem>
                  <MenuItem value="I">INACTIVO</MenuItem>
                </TextField>
              </Grid> */}
            </Grid>
          </Paper>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default CrearCentrosPPE
